import { useCityStore } from '@store/city';
import { getCorrectPathname } from '@utils/getCorrectPathname';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CategoryDetail, ProductDetail, VirtualCategory } from '@api/catalog';
import { useCategoryDetail, useVirtualCategoryDetail } from '@api/catalog/categories';
import { getPagination } from '@api/common/helpers';

import { BlockBreadcrumbs } from '@components/BlockBreadcrumbs';
import CategoryPanel from '@components/CategoryPanel';
import { SortFilter } from '@components/ControlViewItems/types';
import { ExpandableBlock } from '@components/ExpandableBlock';
import { useCatalogFilters } from '@components/Filters/ProductFilter/useCatalogFilters';
import PageWrapper from '@components/PageWrapper';

import { ITEMS_PER_PRODUCTS_PAGE } from '@scripts/constants';
import { VIEW_OPTIONS } from '@scripts/enums';
import { Layout, Section, colors, scale, typography } from '@scripts/gds';
import {
    customFlatWithoutChildrens,
    declOfNum,
    getBreadcrums,
    getIdBySeoType,
    getTotalPages,
    isNotEmptyObject,
} from '@scripts/helpers';
import { useMedia } from '@scripts/hooks';
import { useGetProductsList } from '@scripts/hooks/useGetProductsList';
import { useGetUserData } from '@scripts/hooks/useGetUserData';
import { useRedirectToNotEmptyPage } from '@scripts/hooks/useRedirectToNotEmptyPage';

import { SeoData } from '@api/seo/types';
import { getVirtualFilters } from '../helper';
import VirtualCategories from './VirtualCategories';

const ProductsList = dynamic(() => import('@components/ProductsList'));

const SubCategory = ({
    isVirtual,
    categoryId,
    totalSSR,
    dataProducts,
    seoData,
}: {
    isVirtual?: boolean;
    categoryId?: number | null;
    totalSSR?: number;
    dataProducts?: any;
    seoData?: SeoData;
}) => {
    const productsListDataBack = dataProducts?.data || [];
    const [productDetailsData, setProductDetailsData] = useState<ProductDetail[]>(productsListDataBack || []);
    const [activeView, setActiveView] = useState<string>(VIEW_OPTIONS.DEFAULT);
    const { xs, sm } = useMedia();
    const { asPath, query, replace } = useRouter();
    const currentCity = useCityStore(({ currentCity }) => currentCity);

    const [urls, setUrls] = useState(asPath);

    const pathname = getCorrectPathname(asPath);
    const firstUpdateFilters = useRef(false);

    const seoItemId = getIdBySeoType(pathname, query);
    const { data: { title = '', description = '', h1 = '', seo_text = '' } = {} } = seoData || {};

    const activePage = +(query?.page || 1);

    const categoryCode = useMemo(() => String(isVirtual ? query.code || '' : query.category || ''), [query, isVirtual]);

    const categoryVirualId = categoryId || seoItemId;

    const { apiUser, error: userError, user } = useGetUserData();

    const [sortFilter, setSortFilter] = useState<SortFilter>({
        sort: 'id',
        isSale: false,
        isPreOrder: false,
    });

    const { data: apiDataVirtualCategory } = useVirtualCategoryDetail(
        {
            filter: { code: categoryCode },
            include: ['children', 'children.properties', 'children.property_values', 'properties', 'property_values'],
        },
        Boolean(query.code)
    );

    const virtualCat = useMemo(() => apiDataVirtualCategory?.data, [apiDataVirtualCategory?.data]);

    const { filtersListData, isFilterFetching, preparedFilters, isFiltersAuthFetching } = useCatalogFilters(
        isVirtual ? { virtual_category_id: categoryVirualId } : { category_id: categoryId },
        {
            enabled: Boolean(categoryVirualId || categoryId) && !user,
            enabledAuth: Boolean(categoryVirualId || categoryId) && Boolean(apiUser) && !userError,
        }
    );

    const totalPages = getTotalPages(dataProducts, ITEMS_PER_PRODUCTS_PAGE);

    const hasCategory = categoryVirualId !== undefined || categoryId !== undefined;

    const enabled = hasCategory && !user;
    const enabledAuth = hasCategory && Boolean(apiUser) && !userError;

    const {
        productsList: productsListData,
        total,
        isLoadingProducts,
        isLoadingProductsAuth,
        isFetchingProducts,
        isFetchingProductsAuth,
    } = useGetProductsList(
        {
            sort: sortFilter.sort,
            ...((categoryVirualId || categoryId) !== null && {
                filter: {
                    ...preparedFilters,
                    ...(isVirtual
                        ? { virtual_category_id: categoryVirualId || categoryId }
                        : { category_id: categoryId }),
                    ...(sortFilter.isSale && { has_discount: sortFilter.isSale }),
                    ...(sortFilter.isPreOrder && {
                        is_pre_order: !sortFilter.isPreOrder,
                    }),
                },
            }),
            customer_region_guid: currentCity.city_guid,
            pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, activePage),
            include: ['brand', 'category', 'images', 'attributes', 'seller'],
        },
        { enabled, enabledAuth }
    );

    const {
        data: apiDataCategory,
        error: errorCategory,
        isFetching: isCategoryFetching,
    } = useCategoryDetail(
        {
            ...(isVirtual
                ? {
                      id: Number(
                          virtualCat?.parent_virtual_category?.parent_pim_category_id ||
                              virtualCat?.parent_pim_category_id
                      ),
                  }
                : { code: categoryCode }),
            ...(!isVirtual &&
                isNotEmptyObject(preparedFilters) && {
                    offers_filter: preparedFilters,
                }),
        },
        isVirtual ? Boolean(virtualCat) : true
    );

    const isLoadingProductsList =
        isLoadingProducts &&
        isLoadingProductsAuth &&
        isFilterFetching &&
        isFetchingProducts &&
        isFetchingProductsAuth &&
        isFiltersAuthFetching &&
        isCategoryFetching;

    const pimCat = useMemo(() => apiDataCategory?.data, [apiDataCategory?.data]);
    const category = useMemo(() => (isVirtual ? virtualCat : pimCat), [pimCat, virtualCat]);

    const preExpandedFilters = useMemo(() => {
        if (!virtualCat) return;

        return getVirtualFilters(virtualCat);
    }, [virtualCat]);

    useEffect(() => {
        if (firstUpdateFilters.current) {
            if (!sortFilter.isSale) delete preparedFilters.has_discount;
            if (!sortFilter.isPreOrder) delete preparedFilters.is_pre_order;

            const filters = query?.filters && JSON.parse(String(query.filters));

            if (
                !sortFilter.isSale &&
                query?.filters &&
                Object.keys(preparedFilters).length > 0 &&
                JSON.parse(String(query?.filters)).hasOwnProperty('has_discount')
            ) {
                delete filters.has_discount;
            }

            if (
                !sortFilter.isPreOrder &&
                query?.filters &&
                Object.keys(preparedFilters).length > 0 &&
                JSON.parse(String(query?.filters)).hasOwnProperty('is_pre_order')
            ) {
                delete filters.is_pre_order;
            }

            const sortBySale = query?.filters
                ? JSON.stringify({ ...filters, has_discount: sortFilter.isSale })
                : JSON.stringify({ has_discount: sortFilter.isSale });
            const sortByPreOrder = query?.filters
                ? JSON.stringify({ ...filters, is_pre_order: sortFilter.isPreOrder })
                : JSON.stringify({ is_pre_order: sortFilter.isPreOrder });
            const defaultSort =
                query?.filters && Object.keys(preparedFilters).length > 0 ? JSON.stringify({ ...filters }) : null;

            const queryResult = () => {
                switch (true) {
                    case filters?.isSale:
                        return sortBySale;
                    case filters?.isPreOrder:
                        return sortByPreOrder;
                    default:
                        return defaultSort;
                }
            };

            if (queryResult() !== null) {
                if (query?.category)
                    replace({
                        query: {
                            category: query.category,
                            filters: queryResult(),
                        },
                    });
                else if (query?.code)
                    replace({
                        query: {
                            code: query.code,
                            filters: queryResult(),
                        },
                    });
            } else if (query?.category)
                replace({
                    query: {
                        category: query.category,
                        ...(query.page && { page: query.page }),
                    },
                });
            else if (query?.code)
                replace({
                    query: {
                        code: query.code,
                        ...(query.page && { page: query.page }),
                    },
                });
        }
        firstUpdateFilters.current = true;
    }, [sortFilter, query.filters]);

    useEffect(() => {
        setSortFilter(prev => ({
            ...prev,
            isSale: query.filters ? JSON.parse(String(query.filters)).has_discount : false,
            isPreOrder: query.filters ? JSON.parse(String(query.filters)).is_pre_order : false,
        }));
    }, []);

    const breadcrumbsItems = useMemo(
        () =>
            !isVirtual
                ? [
                      {
                          name: 'Главная',
                          link: '/',
                      },
                      ...getBreadcrums([
                          {
                              name: pimCat?.parents?.name,
                              code: pimCat?.parents?.code,
                          },
                          ...customFlatWithoutChildrens(pimCat?.parents?.children),
                          { code: category?.code, name: category?.name },
                      ]),
                  ]
                : [
                      {
                          name: 'Главная',
                          link: '/',
                      },
                      ...getBreadcrums([
                          {
                              name: pimCat?.parents?.name,
                              code: pimCat?.parents?.code,
                          },
                          ...customFlatWithoutChildrens(pimCat?.parents?.children),
                          { code: pimCat?.code, name: pimCat?.name },
                          ...((virtualCat?.parent_virtual_category && [
                              {
                                  code: `collections/${virtualCat.parent_virtual_category.code}`,
                                  name: virtualCat?.parent_virtual_category.name,
                              },
                          ]) ||
                              []),
                          { code: `collections/${category?.code}`, name: category?.name },
                      ]),
                  ],
        [category]
    );

    useEffect(() => {
        if (productsListData && productsListData.length > 0) {
            const combined = [...productDetailsData, ...productsListData];
            const uniqueProducts = [...new Map(combined.map(item => [item.id, item])).values()];
            setProductDetailsData(uniqueProducts);
        }
    }, [productsListData]);

    useEffect(() => {
        if (asPath !== urls && productsListData && productsListData.length > 0) {
            setProductDetailsData([...productsListData]);
        }
        setUrls(asPath);
    }, [asPath]);

    useEffect(() => {
        if (productsListData && productsListData.length > 0 && activePage === 1) {
            setProductDetailsData([...productsListData]);
        }
    }, [activePage]);

    useRedirectToNotEmptyPage({
        activePage,
        total: totalSSR || total,
        itemsPerPageCount: ITEMS_PER_PRODUCTS_PAGE,
    });

    useEffect(
        () => () => {
            setProductDetailsData([]);
        },
        [pathname]
    );

    return (
        <PageWrapper
            seoProps={{
                activePage: String(activePage),
                totalPages: String(totalPages),
                title:
                    title ||
                    `${
                        (isVirtual ? virtualCat?.full_name : category?.name) || ''
                    } купить в интернет-магазине «SecuMarket»`,
                description:
                    description ||
                    `${category?.name} различных производителей и брендов купить по выгодным ценам с доставкой в любой регион России в интернет-магазине «SecuMarket».`,
            }}
        >
            <Section>
                {!errorCategory && category && (
                    <Layout cols={{ xxxl: 12, sm: 6 }} gap={scale(3)}>
                        {breadcrumbsItems.length > 0 && (
                            <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                <BlockBreadcrumbs items={breadcrumbsItems} />
                            </Layout.Item>
                        )}

                        <Layout.Item
                            css={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                [xs]: { flexDirection: 'column' },
                            }}
                            col={{ xxxl: 12, sm: 6 }}
                        >
                            <h1
                                css={{
                                    ...typography('h1'),
                                    margin: '0.11em 0',
                                    marginRight: scale(2),
                                    [sm]: { margin: '4px' },
                                    [xs]: { margin: 0 },
                                }}
                            >
                                {h1 || (isVirtual ? virtualCat?.full_name : category.name)}
                            </h1>

                            <span css={{ color: colors.textNuanceLight }}>
                                {totalSSR || total}{' '}
                                {declOfNum(isVirtual ? totalSSR || total : (category as CategoryDetail).offers_count, [
                                    'товар',
                                    'товара',
                                    'товаров',
                                ])}
                            </span>
                        </Layout.Item>

                        {!isVirtual &&
                            (category as CategoryDetail).children?.map(panel => (
                                <Layout.Item
                                    key={panel.id}
                                    col={{ xxxl: 2, md: 3, sm: 2 }}
                                    css={{
                                        height: scale(23),
                                    }}
                                >
                                    <CategoryPanel
                                        link={`/catalog/${panel.code}`}
                                        image={panel.image}
                                        name={panel.name}
                                    />
                                </Layout.Item>
                            ))}

                        {isVirtual && category?.children && category?.children?.length > 0 && (
                            <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                <VirtualCategories virtual_categories={(category as VirtualCategory).children!} />
                            </Layout.Item>
                        )}

                        {!isVirtual && pimCat?.virtual_categories?.length ? (
                            <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                <VirtualCategories virtual_categories={pimCat.virtual_categories} />
                            </Layout.Item>
                        ) : null}

                        <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                            <ProductsList
                                activeView={activeView}
                                setActiveView={setActiveView}
                                productsListData={
                                    productsListData?.length !== productDetailsData?.length
                                        ? productDetailsData
                                        : productsListData
                                }
                                totalPages={totalPages}
                                filters={filtersListData}
                                breadcrumbsItems={breadcrumbsItems}
                                isFiltersLoading={isLoadingProductsList}
                                sortFilter={sortFilter}
                                setSortFilter={setSortFilter}
                                preExpandedFilters={preExpandedFilters}
                                setProductDetailsData={setProductDetailsData}
                            />
                        </Layout.Item>

                        <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                            {seo_text && (
                                <ExpandableBlock height={scale(15)}>
                                    <span
                                        css={{ marginTop: scale(6) }}
                                        dangerouslySetInnerHTML={{ __html: seo_text }}
                                    />
                                </ExpandableBlock>
                            )}
                        </Layout.Item>
                    </Layout>
                )}
            </Section>
        </PageWrapper>
    );
};

export default SubCategory;
